// REACT
import { useState, useEffect } from "react";

// MATERIAL UI
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Stack,
  useTheme,
  Pagination,
  Fade,
  Paper,
  Link,
} from "@mui/material";

// STYLES
import { TargetOrderStyles } from "../../styles/TargetOrder.style";

// REDUX
import { useTargetState } from "src/store/features/TargetOrders.slice";
import { useMaintenanceState } from "src/store/features/maintenance.slice";
import { TargetOrderCtRes, useGetTargetOrderCtQuery } from "src/store/features/api/generated";

// UTILS
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { timestampFormatter } from "src/utils/helpers/DateAndTimeHelpers";

//COMPONENTS
import TargetOrdersFilters from "../form-control/TargetOrdersFilters";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import TargetOrderCancel from "src/components/trade/target-orders/target-modals/TargetOrderCancel";
import TargetOrderExecute from "src/components/trade/target-orders/target-modals/TargetOrderExecute";

// ICONS
import TargetIOrderIcon from "src/icons/TargetIOrderIcon";
import TargetCancelReqIcon from "src/icons/TargetCancelReqIcon";
import TargetOrderExecuteIcon from "src/icons/TargetOrderExecuteIcon";

// HOOKS
import usePermission from "src/utils/hooks/usePermission";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

function Row(props: {
  row: TargetOrderCtRes;
  index: number;
  loading?: boolean;
  executeData: React.Dispatch<React.SetStateAction<any>>;
  cancelData: React.Dispatch<React.SetStateAction<any>>;
  cancelModal: React.Dispatch<React.SetStateAction<boolean>>;
  executeModal: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { row, cancelModal, cancelData, index, loading } = props;

  const [open, setOpen] = useState(false);

  const theme = useTheme();
  // Find the object in the row.lines array with a side property that matches the targetType
  const valueOne = row.lines.find(
    (x: any) => x.side === (row.targetType === "buy" ? "Buy" : "Sell"),
  );
  let customerDate = new Date().toLocaleString();
  const valueTwo = row.lines.find(
    (x: any) => x.side === (row.targetType === "buy" ? "Sell" : "Buy"),
  );
  const targetPrice = row.lines.find((item: any) => item.known === true)?.price;

  const withinFivePercentage: boolean =
    row.percentageDifference > -5 && row.percentageDifference < 5;

  const gamOrCanceled = row.gamRequested || row.cancelRequested;

  // Cts enter pending state while netsuite creates the CT
  const ctPending = row.status === "Target Order Requested";

  return (
    <>
      <Fade in={!loading} timeout={{ enter: 400, exit: 2000 }}>
        <TableRow
          key={index}
          sx={{
            background:
              index % 2 ? theme.palette.primary.contrastText : theme.palette.background.paper,
            height: "80px",
            cursor: withinFivePercentage && !gamOrCanceled ? "pointer" : "default",
          }}
          onClick={() => {
            if (withinFivePercentage) {
              if (gamOrCanceled) return;
              setOpen(!open);
              trackEvent("Target_Order_Table_Expand_Warning_Row");
            }
          }}>
          <TableCell sx={{ borderBottom: "none", padding: "0px", minWidth: "40px" }}>
            {withinFivePercentage && !gamOrCanceled ? <TargetIOrderIcon /> : null}
          </TableCell>
          <TableCell sx={{ borderBottom: "none", paddingRight: "0px" }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  borderBottom: "none",
                }}>
                {valueOne?.currencyConfig.code}/{valueTwo?.currencyConfig.code}
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>{timestampFormatter(row.created)}</Typography>
            </Box>
          </TableCell>
          <TableCell sx={{ textTransform: "capitalize", borderBottom: "none" }} align="center">
            {row.targetType}
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }} align="right">
            <Box>
              <Stack direction="row" alignItems="center">
                <CountryOrCryptoFlag
                  name={valueOne?.currencyConfig.logoUrl || valueOne?.currencyConfig.code}
                  size={17}
                />
                <Typography sx={{ marginLeft: "8px", fontSize: "14px" }}>
                  {valueOne?.units}
                </Typography>
              </Stack>
            </Box>
          </TableCell>
          <TableCell
            sx={{
              borderBottom: "none",
              paddingTop: "16px",
              paddingBottom: "16px",
              paddingRight: "10px",
              paddingLeft: "0px",
            }}
            align="left">
            {formatCurrency(targetPrice || 0, valueOne?.quoteCurrencyConfig.code || "USD", {
              type: "format",
            })}
          </TableCell>
          <TableCell sx={{ borderBottom: "none", padding: "16px 0  16px 0 " }} align="left">
            {formatCurrency(row.currentPrice || 0, valueOne?.quoteCurrencyConfig.code || "USD")}
          </TableCell>
          <TableCell
            sx={{
              textAlign: "center",
              borderBottom: "none",
            }}
            align="center">
            {row.cancelRequested && <TargetCancelReqIcon />}
            {row.gamRequested && <TargetOrderExecuteIcon />}
            {!gamOrCanceled && (
              <Typography
                sx={{
                  borderBottom: "none",
                  fontSize: "14px",
                  textAlign: "center",
                  textTransform: "capitalize",
                }}>
                {(row.percentageDifference ?? 0).toFixed(2) + `%`}
              </Typography>
            )}
          </TableCell>
          {row.cancelRequested && (
            <TableCell sx={{ borderBottom: "none", paddingLeft: "0px" }} align="left" colSpan={2}>
              <Typography
                sx={{
                  borderBottom: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  textAlign: "center",
                  textTransform: "capitalize",
                }}
                align="center">
                Requested to Cancel
              </Typography>
            </TableCell>
          )}
          {ctPending && !gamOrCanceled && (
            <TableCell sx={{ borderBottom: "none", paddingLeft: "0px" }} align="left" colSpan={2}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  textTransform: "capitalize",
                  paddingLeft: "23px",
                }}
                align="left">
                Pending
              </Typography>
            </TableCell>
          )}
          {!gamOrCanceled && !ctPending && (
            <TableCell sx={{ borderBottom: "none", paddingLeft: "16px" }} align="right">
              <Button
                disableRipple
                onClick={(e) => {
                  e.stopPropagation();
                  trackEvent("Target_Order_Open_Cancel_Modal");
                  cancelData({
                    ctId: row.id,
                    datetime: customerDate,
                    type: row.targetType,
                    tradeUnits: valueOne?.units,
                    currentPrice: row.currentPrice,
                    targetPrice: targetPrice,
                    currencyConvertCode: row.quoteCurrencyConfig.code,
                    currencyTradeCode: valueOne?.currencyConfig.code,
                  });
                  cancelModal(true);
                }}
                sx={{
                  width: "68px",
                  height: "25px",
                  borderRadius: "20px",
                  textTransform: "capitalize",
                  fontSize: "14px",
                  backgroundColor: theme.palette.primary.light,
                }}
                variant="outlined">
                Cancel
              </Button>
            </TableCell>
          )}
        </TableRow>
      </Fade>
    </>
  );
}
type PaginationParams = { limit: number; page: number };
export default function CollapsibleTable() {
  const { targetorderscontroller } = useMaintenanceState();
  const [cancelModal, setCancelModal] = useState(false);
  const [executeModal, setExecuteModal] = useState(false);
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    limit: 10,
    page: 1,
  });
  const [executeData, setExecuteData] = useState(false);
  const [cancelData, setCancelData] = useState({
    ctId: "",
    datetime: "",
  });
  const classes = TargetOrderStyles();
  const { filterOption, filterToken, sortOption, ctSuccess } = useTargetState();
  const { data, isLoading, refetch, isFetching } = useGetTargetOrderCtQuery(
    {
      limit: 7,
      page: paginationParams.page,
      sortType: sortOption,
      filterType: filterOption,
      tokenCode: filterToken,
    },
    { skip: !targetorderscontroller },
  );

  const theme = useTheme();
  const { isGuest } = usePermission();

  const handlePagination = (event: unknown, newPage: number): void => {
    setPaginationParams((prev: PaginationParams): PaginationParams => ({ ...prev, page: newPage }));
  };

  useEffect(() => {
    // refetch on change.
    if (targetorderscontroller) {
      [ctSuccess, sortOption, filterOption].some((x) => x) && refetch();
    }
  }, [ctSuccess, sortOption, filterOption]);

  if (isLoading || !data || isFetching)
    return (
      <Paper
        sx={{
          height: "753px",
          borderRadius: "20px",
          boxShadow: " 0px 4px 20px rgba(173, 192, 203, 0.3)",
          position: "relative",
          paddingTop: "25px",
        }}>
        <TableContainer sx={{ overflow: "hidden" }}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ minWidth: "10px" }}
                  className={classes.tableRow}
                  align="center"></TableCell>
                <TableCell className={classes.tableRow} align="center">
                  order
                </TableCell>
                <TableCell className={classes.tableRow} align="center">
                  type
                </TableCell>
                <TableCell className={classes.tableRow} align="left">
                  units
                </TableCell>
                <TableCell
                  className={classes.tableRow}
                  sx={{
                    padding: "16px  19px   16px 0 ",
                    minWidth: "80px",
                  }}
                  align="center">
                  Target <br /> Price
                </TableCell>
                <TableCell
                  className={classes.tableRow}
                  sx={{
                    padding: "16px 19px   16px 0 ",
                    minWidth: "80px",
                  }}
                  align="center">
                  Current
                  <br /> Price
                </TableCell>
                <TableCell className={classes.tableRow} align="center">
                  Target <br /> Distance
                </TableCell>
                <TableCell
                  sx={{ minWidth: "70px" }}
                  className={classes.tableRow}
                  align="center"></TableCell>
                <TableCell
                  colSpan={2}
                  sx={{ minWidth: "70px" }}
                  className={classes.tableRow}
                  align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody></TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            height: "75%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <LoadingSpinner size={60} />
        </Box>
      </Paper>
    );

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: "-42px",
          right: "0px",
          [theme.breakpoints.down(1366)]: {
            top: "0px",
            zIndex: "10",
          },
        }}>
        <TargetOrdersFilters disabled={Boolean(!data?.data.length)} />
      </Box>

      <Paper
        sx={{
          height: "753px",
          borderRadius: "20px",
          boxShadow: " 0px 4px 20px rgba(173, 192, 203, 0.3)",
          position: "relative",
          paddingTop: "25px",
        }}>
        {!isGuest && (
          <>
            <TargetOrderCancel
              cancelReq={cancelData}
              refetchData={refetch}
              open={cancelModal}
              handleClose={setCancelModal}
            />
            <TargetOrderExecute
              open={executeModal}
              refetchData={refetch}
              executeData={executeData}
              handleClose={setExecuteModal}
            />
          </>
        )}

        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableRow} align="center"></TableCell>
                <TableCell className={classes.tableRow} align="center">
                  order
                </TableCell>
                <TableCell className={classes.tableRow} align="center">
                  type
                </TableCell>
                <TableCell className={classes.tableRow} align="left">
                  units
                </TableCell>
                <TableCell
                  className={classes.tableRow}
                  sx={{ padding: "16px 19px  16px 0 " }}
                  align="center">
                  Target <br /> Price
                </TableCell>
                <TableCell
                  className={classes.tableRow}
                  sx={{ padding: "16px 19px  16px 0 " }}
                  align="center">
                  Current
                  <br /> Price
                </TableCell>
                <TableCell className={classes.tableRow} align="center">
                  Target <br /> Distance
                </TableCell>
                <TableCell className={classes.tableRow} align="center"></TableCell>
                <TableCell className={classes.tableRow} align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody id="target-order-tablebody">
              {data.data.length
                ? data.data.map((row: TargetOrderCtRes, index: number) => (
                    <Row
                      key={index}
                      index={index}
                      loading={isLoading}
                      row={row}
                      cancelModal={setCancelModal}
                      executeModal={setExecuteModal}
                      executeData={setExecuteData}
                      cancelData={setCancelData}
                    />
                  ))
                : ""}
            </TableBody>
          </Table>
          {!data.data.length && (
            <>
              <Box
                sx={{
                  height: "408px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <Typography>
                  You currently have no open target open with us. Place a target on the left and
                  track your order here
                </Typography>
              </Box>
              <Box
                sx={{
                  height: "150px",
                  backgroundColor: "#f8f9f9",
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "16px",
                  marginRight: "16px",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <Stack sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                    Not sure what a target is or how to place one?
                  </Typography>
                  <Typography sx={{ fontSize: "14px" }}>
                    Speak to your broker here or view our quick links below
                  </Typography>
                </Stack>
                <Stack
                  sx={{
                    marginTop: "20px",
                    color: theme.palette.secondary.main,
                  }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    <Link
                      href="https://support.calebandbrown.com/en/articles/11182116-how-to-place-a-target-order-limit-order"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: theme.palette.secondary.main }}>
                      How to place a target order?
                    </Link>
                  </Typography>
                </Stack>
              </Box>
            </>
          )}
          {data.data.length ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                paddingTop: "27px",
                bottom: "20px",
              }}>
              <Pagination
                count={data?.totalPages}
                page={data?.currentPage}
                onChange={handlePagination}
                showFirstButton
                showLastButton
              />
            </Box>
          ) : (
            <span></span>
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
}
